// 弾いたのに自分が崩れた瞬間の見た目 (2026-09-22 体幹の票 案A・全ボス共通)。
//   通常パリィで自分の体幹が溢れた弾きは、攻撃は防げているが自分が崩れる。今まではここにも白い成功の光が出て、
//   「弾けたのに急にやられた」と読めていた (Codex 実機の総評 09-21 優先1)。成功の光の代わりに「押し負けた」を見せる。
//   票 = docs/handoffs/2026-09-22-player-posture-break-on-parry-ticket.md。判定・数値は一切変えない (描くのと音だけ)。
//
//   比較 = DEV の `?parryBreakFx=now|a|b|c`。
//     now = 今まで (白い成功の光と弾きの音)
//     a   = 砕け：朱の光が潰れ、弾きの輪が割れて飛び散り、刃の光が手前へ砕け落ちる。弾きの音の代わりに割れる音
//     b   = a ＋ 画面の縁が一瞬締まる＋止めを長く (被弾と同じ「やられた」の手触りを重ねる)
//     c   = a から輪の欠片を抜いたもの (朱の光と刃の欠片だけ・輪が線に見える場合の控え)
//     ab  = 2026-10-05 作り直し (既定)：画面の色が抜ける＋朱の火の粉の尾 (描画は parry_family_fx.ts・画面の縁の赤みは外す)
//   縛り＝線 (stroke) で描かない。面と粒で作る (awoken_rampage_fx.ts 冒頭と同じ)。朱は自分の体幹の危険の色
//   (PALETTE.hudPlayerPostureDanger) と揃え、「体幹が尽きた」と体幹バーの警告を結ぶ。
import { glowMote, hash01 } from './fx_kit'
import { drawParryBreakAB } from './parry_family_fx'

export type ParryBreakLook = 'now' | 'a' | 'b' | 'c' | 'ab'
const LOOKS: readonly string[] = ['now', 'a', 'b', 'c', 'ab']
// 既定 = ab (2026-10-05 ユーザー裁定・見比べ版4/5 の弾き崩し A+B)。09-22 の既定 b と外した案は DEV の `?parryBreakFx=now|a|b|c` に残す。
let look: ParryBreakLook = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'ab'
  const v = new URLSearchParams(window.location.search).get('parryBreakFx')
  return LOOKS.includes(v ?? '') ? v as ParryBreakLook : 'ab'
})()
export function setParryBreakLook(l: ParryBreakLook): void { look = l }
export function getParryBreakLook(): ParryBreakLook { return look }

export const PARRY_BREAK_LIFE_SEC = 0.5   // game.ts の FX_LIFETIME_SEC と同じ (それより長くは残らない)
const clamp01 = (v: number): number => v < 0 ? 0 : v > 1 ? 1 : v

/** x,y = 掲げた刀身 (parry_spark と同じ点)。dir = 主人公の向き (+1 右)。破片はボスと逆 (手前) へ砕け落ちる。 */
export function drawParryBreak(ctx: CanvasRenderingContext2D, x: number, y: number, dir: number, age: number, seed: number): void {
  if (look === 'ab') { drawParryBreakAB(ctx, x, y, dir, age); return }
  if (age < 0 || age > PARRY_BREAK_LIFE_SEC) return
  const k = clamp01(age / PARRY_BREAK_LIFE_SEC)
  const fade = 1 - k
  // 朱の光: 一瞬ふくらんで、すぐ潰れる (成功の光は広がって残る。こちらは縮んで消える＝押し負け)。
  const pop = age < 0.05 ? age / 0.05 : Math.max(0, 1 - (age - 0.05) / 0.2)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  glowMote(ctx, x, y, 70 * pop + 20, 'rgb(224,80,58)', 0.85 * pop)
  glowMote(ctx, x, y, 26 * pop + 6, 'rgb(255,214,190)', 0.95 * pop)
  ctx.restore()
  ctx.save()
  // 弾きの輪が割れる: 輪の欠片 (面の弧) が外へ飛び、手前へ落ちる。成功の光の「輪」が砕けた、と読ませる。
  const ringR = 24 + 30 * (1 - (1 - Math.min(1, age / 0.12)) ** 2)
  for (let i = 0; i < (look === 'c' ? 0 : 7); i++) {
    const h = hash01(seed + i * 3.7)
    const mid = (i / 7) * Math.PI * 2 + h * 0.4
    const half = 0.26 + h * 0.12
    const fly = age * (120 + h * 140)
    const ox = Math.cos(mid) * fly - dir * age * 90
    const oy = Math.sin(mid) * fly + 620 * age * age
    const w = 7 * (1 - k * 0.6)
    const col = i % 2 ? 'rgba(255,236,226,' : 'rgba(236,104,80,'
    ctx.fillStyle = col + (0.95 * fade).toFixed(3) + ')'
    ctx.beginPath()
    ctx.arc(x + ox, y + oy, ringR + w / 2, mid - half, mid + half)
    ctx.arc(x + ox, y + oy, Math.max(1, ringR - w / 2), mid + half, mid - half, true)
    ctx.closePath(); ctx.fill()
  }
  // 刃の光の欠片: 鋼の色の三角が手前へ弾かれ、重力で落ちる。
  for (let i = 0; i < 14; i++) {
    const h = hash01(seed + i * 5.1), h2 = hash01(seed + i * 2.3 + 7)
    const a = Math.PI + (h - 0.5) * 1.8            // 手前 (ボスと逆) を中心に上下へ散る
    const v = 200 + h2 * 260
    const px = x + dir * Math.cos(a) * v * age
    const py = y + Math.sin(a) * v * age * 0.8 - 80 * age + 900 * age * age
    const sz = 4 + h2 * 5
    const rot = seed + i + age * (10 + h * 12) * (i % 2 ? 1 : -1)
    ctx.fillStyle = i % 3 === 0
      ? `rgba(240,112,86,${(0.95 * fade).toFixed(3)})`
      : `rgba(${i % 2 ? '226,222,234' : '168,164,180'},${(0.95 * fade).toFixed(3)})`
    ctx.beginPath()
    for (let c = 0; c < 3; c++) {
      const ca = rot + c * 2.094, rr = sz * (c === 0 ? 1.4 : 0.55)
      if (c === 0) ctx.moveTo(px + Math.cos(ca) * rr, py + Math.sin(ca) * rr)
      else ctx.lineTo(px + Math.cos(ca) * rr, py + Math.sin(ca) * rr)
    }
    ctx.closePath(); ctx.fill()
  }
  ctx.restore()
}
